/**
 * JourneyCallbackOffer.tsx — "Rather speak with a licensed clinician?" on every step of the
 * journey (CTO, 2026-09-11: "an option to request a call back and enter
 * preferred call times… anytime").
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * THE EXISTING CALLBACK LANE, OPENED IN PLACE — NOT A SECOND ONE. The dialog
 * mounts the same `CallbackForm` the standalone `/:clinicSlug/callback` page and
 * every stop screen use, so the request is the same `POST /v1/clinic/callbacks`,
 * checked by clinic-service against the same five time blocks, and the patient
 * gets the same `callback_confirmation` email. Nothing here talks to the API.
 *
 * WHY A DIALOG AND NOT A LINK TO THE CALLBACK PAGE. The journey lives in this
 * route's memory: the current question, the answers so far, the checkout step.
 * Navigating to `/:clinicSlug/callback` unmounts all of it, and that page's Back
 * replaces onto a fresh journey, so asking for a call part-way through would
 * have cost the patient their place. The dialog changes nothing underneath it —
 * close it and they are exactly where they were.
 *
 * THE LEAD RIDES WITH IT. With the journey's `leadId`, clinic-service attaches
 * the ticket to the patient's existing deal, so whoever rings can see where they
 * stopped, and the form does not ask for an email it already has. Before there
 * is a lead (Step 0) the form asks for one, exactly as the standalone page does.
 *
 * WHAT THE SERVER DOES WITH A MID-JOURNEY REQUEST (clinic-service deal hooks,
 * unchanged): an unpaid deal moves to the callback lane; a patient who has
 * already AUTHORISED a card stays in the clinician's queue with the ticket
 * attached. If they carry on and pay, the order sync moves them into review as
 * normal. Nothing on this surface can put anyone in front of a clinician.
 *
 * WHO OWNS `requested`. JourneyPage builds this once and keeps the flag, so a
 * patient who has asked is not offered it again on the next screen (a second
 * request would mint a second ticket). It is never mounted beside a stop
 * screen, which carries the form inline already.
 *
 * OPENED FROM ELSEWHERE ON THE SCREEN (CTO, 2026-09-24). Under QuestionFlow the
 * offer also PUBLISHES its `open()` (JourneyCallbackContext.tsx), so the "Why we
 * ask" dialog can offer the same "Request a free call back": it closes itself
 * and calls `open()` in the same click, and THIS dialog — the same form, the
 * same POST, the same lead — is the one that appears. One dialog at a time,
 * never a second form. Closed without a send, focus goes back to the control
 * the caller named (the "Why we ask" link); after a send it goes to the
 * thank-you line exactly as it does when the offer itself opened the dialog.
 * `open()` does nothing once a request has been sent: a second request would
 * mint a second ticket.
 *
 * IT ALSO PUBLISHES WHETHER IT IS OPEN, AND ITS OWN `close()` (review,
 * 2026-09-25), for the journey leave dialog: Back pressed while this dialog is
 * up (on Android, the usual way to dismiss it) closes THIS dialog, as "Not now"
 * would, instead of stacking a second leave dialog underneath it.
 *
 * R28 / Part F: category-level copy only, and nothing goes to the dataLayer.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { Attribution } from '../api/types';
import { DialogShell } from '../ui/ConfirmDialog';
import { Button } from '../ui/kit';
import { CallbackForm } from './CallbackForm';
import { usePublishJourneyCallback } from './JourneyCallbackContext';

/**
 * The offer's words, shared with the "Why we ask" dialog's call-back row, the
 * "Leave this step?" dialog and the /unlock page, so none of them can drift into
 * a different promise.
 *
 * 2026-09-26 (Ashley Haveloch-Jones, via the CTO): "Rather talk it through?"
 * became "Rather speak with a licensed clinician?" — it says WHO the call is
 * with, which the old line left to the dialog. It stays true because the free
 * call back is clinician-led (the header's CTA already says "Speak to a
 * clinician now"). Keep it category-level (R28): a clinician, never a
 * treatment.
 */
export const CALLBACK_OFFER_LEAD = 'Rather speak with a licensed clinician?';
export const CALLBACK_OFFER_ACTION = 'Request a free call back';
export const CALLBACK_OFFER_THANKS =
  'Thanks — we’ll call you at the time you chose. You can carry on here in the meantime.';

/** A text-weight control: an offer, not a second call to action on the screen. */
const offerButton: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '4px 2px',
  margin: 0,
  font: 'inherit',
  fontWeight: 600,
  color: 'var(--gps-green-text)',
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  cursor: 'pointer',
};

export function JourneyCallbackOffer({
  clinicSlug,
  attribution,
  leadId,
  requested,
  onRequested,
}: {
  clinicSlug: string;
  attribution?: Attribution;
  /** The journey's lead, when there is one — links the ticket to the patient's deal. */
  leadId?: string;
  /** True once this journey has sent a request; JourneyPage owns it. */
  requested: boolean;
  onRequested: () => void;
}) {
  const [open, setOpen] = useState(false);
  // Sent from THIS dialog, as opposed to arriving with `requested` already true.
  const [sentHere, setSentHere] = useState(false);
  const thanksRef = useRef<HTMLParagraphElement>(null);
  // Set when the dialog closes on a sent request: the button that opened it has
  // been replaced by the thank-you line, so focus has nowhere to return to.
  // Moving it to that line tells a screen-reader user where they are, and
  // keeps the keyboard from being dropped at the top of the page.
  const focusThanks = useRef(false);
  // Set only when the dialog was opened from ELSEWHERE (the "Why we ask"
  // dialog): the node that was focused when it opened lived in a dialog that has
  // since closed, so DialogShell's own focus return lands on nothing. Runs after
  // DialogShell's cleanup (a child's), so this is the focus that sticks.
  const returnFocusTo = useRef<HTMLElement | null>(null);
  const requestedRef = useRef(requested);
  requestedRef.current = requested;

  useEffect(() => {
    if (open) return;
    const target = returnFocusTo.current;
    returnFocusTo.current = null;
    if (focusThanks.current) {
      focusThanks.current = false;
      thanksRef.current?.focus();
      return;
    }
    if (target?.isConnected) target.focus();
  }, [open]);

  const openFromElsewhere = useCallback((focusOnClose?: HTMLElement | null) => {
    if (requestedRef.current) return;
    returnFocusTo.current = focusOnClose ?? null;
    setOpen(true);
  }, []);
  // "Not now" / "Back to where I was", the scrim and Escape — and the journey
  // leave dialog when Back is pressed with this dialog up. A `useCallback`, so
  // it can be published without republishing on every render.
  const close = useCallback(() => {
    if (sentHere) focusThanks.current = true;
    setOpen(false);
  }, [sentHere]);
  usePublishJourneyCallback({ requested, open: openFromElsewhere, isOpen: open, close });

  const showThanks = requested && !open;

  return (
    <div
      style={{
        textAlign: 'center',
        marginTop: 'var(--gps-s-6)',
        fontSize: '14px',
        color: 'var(--gps-muted)',
        lineHeight: 1.5,
      }}
    >
      {showThanks ? (
        <p ref={thanksRef} tabIndex={-1} style={{ margin: 0 }}>
          {CALLBACK_OFFER_THANKS}
        </p>
      ) : (
        <p style={{ margin: 0 }}>
          {CALLBACK_OFFER_LEAD}{' '}
          <button type="button" style={offerButton} aria-haspopup="dialog" onClick={() => setOpen(true)}>
            {CALLBACK_OFFER_ACTION}
          </button>
        </p>
      )}
      {open ? (
        // `trapFocus`: this dialog also opens from "Why we ask", over a question
        // whose answers submit on one key; Tab must not reach them from here.
        <DialogShell title={CALLBACK_OFFER_ACTION} onCancel={close} width="min(480px, 100%)" trapFocus>
          {sentHere ? null : (
            <p style={{ margin: '0 0 var(--gps-s-3)', color: 'var(--gps-muted)', fontSize: '14px' }}>
              Tell us when suits you and one of our team will ring you &mdash; free, with no pressure. You
              won&rsquo;t lose your place: close this whenever you like and carry on.
            </p>
          )}
          <CallbackForm
            clinicSlug={clinicSlug}
            attribution={attribution}
            leadId={leadId}
            onSent={() => {
              setSentHere(true);
              onRequested();
            }}
          />
          <div style={{ marginTop: 'var(--gps-s-3)' }}>
            <Button type="button" variant="ghost" onClick={close} style={{ width: 'auto' }}>
              {sentHere ? 'Back to where I was' : 'Not now'}
            </Button>
          </div>
        </DialogShell>
      ) : null}
    </div>
  );
}
